import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "../../lib/api";
import { Badge, DataTable, Panel, Select, Stat, eurOf, money, shortDate, stamp, statusTone, useEurRate, useTable } from "./ui";

const LABELS = {
  revenue: "Revenue", delivery: "Delivery fees", payout: "Partner payouts",
  discounts: "Discounts given", orders: "Orders counted", gateway_fees: "Card fees total",
  gateway_fees_florist: "Card fees on shops", gateway_fees_platform: "Card fees on us",
  refunds: "Refunded", net_margin: "Net margin",
};

export default function FinancePage() {
  const [fin, setFin] = useState(null);
  const [msg, setMsg] = useState("");
  const table = useTable("/admin/tables/payouts");
  const fx = useEurRate();

  const load = useCallback(() => api.get("/admin/finance").then((r) => setFin(r.data)).catch((e) => setMsg(errText(e))), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, done) => {
    setMsg("");
    try {
      await fn();
      setMsg(done);
      await load();
      table.reload();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  const statement = async (id) => {
    const r = await api.get(`/admin/payouts/${id}/statement`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "text/html" })), "_blank");
  };

  const columns = [
    { key: "created_at", label: "Created", sortable: true, render: (r) => <span className="text-xs whitespace-nowrap">{stamp(r.created_at)}</span> },
    { key: "shop_name", label: "Shop", render: (r) => (
      <Link to={`/admin/florists/${r.florist_id}?tab=payouts`} className="underline decoration-brass underline-offset-2">{r.shop_name}</Link>
    ) },
    { key: "period_end", label: "Period", sortable: true, render: (r) => <span className="text-xs whitespace-nowrap">{shortDate(r.period_start)} → {shortDate(r.period_end)}</span> },
    { key: "orders", label: "Orders", render: (r) => <span className="tabular">{r.orders}</span> },
    { key: "gross_mad", label: "Gross", render: (r) => <span className="tabular whitespace-nowrap">{money(r.gross_mad)}</span> },
    { key: "gateway_fees_mad", label: "Card fees", render: (r) => <span className="tabular">−{money(r.gateway_fees_mad)}</span> },
    { key: "net_mad", label: "Net", sortable: true, render: (r) => (
      <span className="tabular whitespace-nowrap">{money(r.net_mad)} MAD <span className="text-brass">{eurOf(fx, r.net_mad)}</span></span>
    ) },
    { key: "status", label: "Status", sortable: true, render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
    { key: "actions", label: "", render: (r) => (
      <span className="flex gap-1 whitespace-nowrap">
        <button className="btn-ghost text-[11px]" data-testid={`statement-${r.id}`} onClick={() => statement(r.id)}>Statement</button>
        {r.status !== "paid" ? (
          <button className="btn-ghost text-[11px]" data-testid={`pay-${r.id}`}
                  onClick={() => act(() => api.post(`/admin/payouts/${r.id}/paid`, { transfer_reference: `VIR-${Date.now()}` }), "Marked as paid.")}>
            Mark paid
          </button>
        ) : <span className="text-[11px] text-muted">{r.transfer_reference}</span>}
      </span>
    ) },
  ];

  return (
    <div className="space-y-6" data-testid="admin-finance">
      {msg ? <p className="text-sm text-brass" data-testid="finance-message">{msg}</p> : null}
      {fin ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(fin.totals).map(([k, v]) => (
              <Stat key={k} testid={`finance-${k}`} label={LABELS[k] || k.replace(/_/g, " ")}
                    value={k === "orders" ? v : `${money(v)} MAD`} tone={k === "net_margin" ? undefined : undefined} />
            ))}
          </div>

          <Panel title="Owed to shops right now" testid="finance-balances"
                 action={<button className="btn-primary text-xs py-2 px-3" data-testid="run-payouts"
                                 onClick={() => act(() => api.post("/admin/payouts/run", { period_start: new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10), period_end: new Date().toISOString().slice(0, 10) }), "Payout run created.")}>
                   Run this week's payouts
                 </button>}>
            <div className="grid md:grid-cols-2 gap-x-8">
              {fin.balances?.map((b) => (
                <p key={b.florist_id} className="flex justify-between text-sm border-b border-brass-light py-1.5" data-testid={`balance-${b.florist_id}`}>
                  <Link className="underline decoration-brass underline-offset-2" to={`/admin/florists/${b.florist_id}`}>{b.shop_name}</Link>
                  <span className="tabular">{money(b.owed_mad)} MAD · {b.orders} orders</span>
                </p>
              ))}
            </div>
          </Panel>
        </>
      ) : <p className="text-sm text-muted">Loading the books…</p>}

      <div className="space-y-2">
        <h3 className="smallcaps">Weekly payouts {table.data?.pending ? `· ${table.data.pending.count} pending, ${money(table.data.pending.amount_mad)} MAD` : ""}</h3>
        <DataTable table={table} columns={columns} testid="payouts-table" name="payouts" searchPlaceholder="Shop or transfer ref…"
                   filters={<Select testid="payouts-filter-status" placeholder="Any status" value={table.params.status}
                                    onChange={(v) => table.set({ status: v })} options={["pending", "paid"]} />} />
      </div>
    </div>
  );
}
